// Prosjektmodulen — inngangspunkt og skall.
//
// Ansvaret her er lite med vilje: velg rute, hold sesjonen, tegn rammen rundt,
// og gi hver skjerm den samme `Kontekst`. All domenelogikk ligger i src/lib,
// alle nettverkskall i src/api, og hver skjerm eier bare sin egen DOM.
import './style.css';
import { lagKlient, type Klient } from './api/klient.js';
import type { MegSvar } from './api/typer.js';
import {
  lesRute, hashFor, synligeVisninger, visningAv, VISNING_NAVN, type Visning,
} from './lib/rute.js';
import { glemToken, lagreToken, lesToken, nettleserLagre } from './lib/token-lager.js';
import { div, knapp, sett, span } from './ui/dom.js';
import { ikon, type Ikonnavn } from './ui/ikoner.js';
import { kvittering, lagKunngjører } from './ui/tilstand.js';
import { visInnlogging } from './skjermer/innlogging.js';
import { visRegistrering } from './skjermer/registrer.js';
import { visTimer } from './skjermer/timer.js';
import { visKanban } from './skjermer/kanban.js';
import { visSprinter } from './skjermer/sprinter.js';
import { visSammendrag } from './skjermer/sammendrag.js';
import { visInviter } from './skjermer/inviter.js';
import { visMeg } from './skjermer/meg.js';
import type { Kontekst } from './skjermer/kontekst.js';

// Hvor API-et ligger. Tom verdi = samme opphav, der Caddy ruter /api videre —
// se deploy/prosjekt.Dockerfile, som mater inn denne ved bygg.
const API_BASIS = (import.meta.env.VITE_DUGNAD_API || '/api').replace(/\/+$/, '');
/** Bygg-identitet, samme rolle som i COP: gjør en deploy visuelt verifiserbar. */
const BYGG_ID = import.meta.env.VITE_BUILD_ID || 'lokal';

const IKON_FOR: Record<Visning, Ikonnavn> = {
  timer: 'timer', kanban: 'kanban', sprinter: 'sprint', sammendrag: 'sammendrag',
  inviter: 'inviter', meg: 'person',
};

const app = document.getElementById('app');
if (!app) throw new Error('mangler #app i index.html');

const lagre = nettleserLagre();
const kunngjører = lagKunngjører();
document.body.append(kunngjører.rot);

start();

function start(): void {
  const rute = lesRute(location.pathname, location.search, location.hash);

  // Registrering er OFFENTLIG og skal aldri gå via sesjonen. En ny frivillig
  // som havner på innloggingen fordi hun ikke har token, har ingen vei videre.
  if (rute.slag === 'registrer') {
    visRegistrering(app!, lagKlient({ basis: API_BASIS }), rute.kode);
    return;
  }

  const lagret = lesToken(lagre);
  if (!lagret) return tilInnlogging();
  void åpne(lagret.token, lagret.husket);
}

function tilInnlogging(beskjed?: string): void {
  visInnlogging(app!, {
    basis: API_BASIS,
    beskjed,
    påInnlogget(token, husk, meg) {
      lagreToken(lagre, token, husk);
      byggSkall(token, meg);
    },
  });
}

/** Har vi et lagret token, må det prøves før vi tegner en innlogget app. */
async function åpne(token: string, husket: boolean): Promise<void> {
  sett(app!, lasteskjerm());
  const klient = lagKlient({ basis: API_BASIS, token: () => token });
  const r = await klient.meg();
  if (r.ok && r.data?.person?.id) {
    // Skriv tilbake med samme «husk»-valg, så tokenet ikke vandrer mellom lagrene.
    lagreToken(lagre, token, husket);
    return byggSkall(token, r.data);
  }
  glemToken(lagre);
  tilInnlogging(r.ok
    ? 'Tjenesten svarte uten person. Logg inn på nytt.'
    : r.feil.art === 'uautentisert'
      ? 'Tokenet er ikke gyldig lenger. Logg inn på nytt.'
      : `Kom ikke i kontakt med tjenesten: ${r.feil.melding}`);
}

function lasteskjerm(): HTMLElement {
  const rot = div('startskjerm');
  rot.setAttribute('aria-busy', 'true');
  rot.append(span('reg-logo', 'PROSJEKT'));
  rot.append(span('startskjerm-tekst', 'Kobler til …'));
  return rot;
}

// ══ Skallet ═══════════════════════════════════════════════════════════════
function byggSkall(token: string, meg: MegSvar): void {
  let nåværende: MegSvar = meg;
  /**
   * Skjermens vakt mot å bli forlatt. Settes av skjermen selv (se
   * `Kontekst.settNavigasjonsvakt`) og nullstilles ved hvert skjermbytte, slik
   * at en skjerm som glemmer å rydde ikke kan låse navigasjonen etterpå.
   */
  let navvakt: (() => boolean) | null = null;
  const slippNavigasjon = (): boolean => !navvakt || navvakt();

  const klient: Klient = lagKlient({
    basis: API_BASIS,
    token: () => token,
    påUautentisert() {
      // Tokenet er trukket tilbake mens vi jobbet. Da hjelper ingen omlasting —
      // brukeren må inn med et nytt, og det skal hun få vite med en gang.
      glemToken(lagre);
      tilInnlogging('Tokenet ble avvist av tjenesten. Logg inn på nytt.');
    },
  });

  const skall = div('skall');
  const topp = div('skall-topp');
  const merkeEl = div('skall-merke');
  merkeEl.append(span('skall-logo', 'PROSJEKT'));
  merkeEl.append(span('skall-under', 'Frivillighetstimer — Heimevernet'));
  topp.append(merkeEl);
  topp.append(div('skall-rom'));

  const personchip = div('personchip');
  topp.append(personchip);
  topp.append(span('bygg-id', `bygg ${BYGG_ID}`));
  skall.append(topp);

  const sidenav = document.createElement('nav');
  sidenav.className = 'skall-side';
  sidenav.setAttribute('aria-label', 'Hovednavigasjon');
  skall.append(sidenav);

  const hoved = document.createElement('main');
  hoved.className = 'skall-hoved';
  hoved.id = 'hoved';
  hoved.tabIndex = -1;
  skall.append(hoved);

  sett(app!, skall);

  const ctx: Kontekst = {
    klient,
    get meg() { return nåværende; },
    kunngjør: (m) => kunngjører.si(m),
    kvitter: (m, v) => kvittering(document.body, m, v),
    async oppdaterMeg() {
      const r = await klient.meg();
      if (r.ok && r.data?.person?.id) {
        nåværende = r.data;
        tegnPersonchip();
        // Rettigheten kan ha endret seg, og navigasjonen viser bare det brukeren
        // faktisk kan bruke — derfor tegnes den om her, ikke bare ved oppstart.
        tegnNav();
      }
    },
    gåTil(v) {
      if (!slippNavigasjon()) return;
      location.hash = hashFor(visningAv(`#${v}`));
    },
    settNavigasjonsvakt(vakt) { navvakt = vakt; },
  };

  const navknapper = new Map<Visning, HTMLButtonElement>();

  const hjelp = div('nav-fot');
  hjelp.append(span('nav-fot-tittel', 'Om tallene'));
  hjelp.append(span('nav-fot-tekst',
    'Timer avrundes ÉN gang, til slutt. «Meldt» er summen av alle økter; «faktisk» '
    + 'teller overlappende timer én gang.'));

  tegnPersonchip();
  tegnNav();
  addEventListener('hashchange', tegnVisning);
  tegnVisning();

  function tegnPersonchip(): void {
    sett(personchip);
    personchip.append(ikon('person'));
    personchip.append(span('personchip-id', nåværende.person.id));
    if (nåværende.person.kanAttestere) personchip.append(span('personchip-rolle', 'attestant'));
  }

  /**
   * Navigasjonen viser BARE visninger denne brukeren kan bruke — «Inviter» krever
   * attestasjonsrett. Gatingen her er kosmetisk: tjenesten håndhever rettigheten
   * autoritativt (VALE §4). Skriver noen `#inviter` i adressen likevel, forklarer
   * skjermen rolig hvorfor den ikke kan brukes, framfor å omdirigere i stillhet.
   */
  function tegnNav(): void {
    sett(sidenav);
    navknapper.clear();
    for (const v of synligeVisninger(nåværende.person)) {
      const b = knapp(VISNING_NAVN[v], () => {
        if (!slippNavigasjon()) return;
        location.hash = hashFor(v);
      }, { variant: 'stille', ikon: ikon(IKON_FOR[v]) });
      b.classList.add('navknapp');
      navknapper.set(v, b);
      sidenav.append(b);
    }
    sidenav.append(hjelp);
    merkAktiv(visningAv(location.hash));
  }

  function merkAktiv(v: Visning): void {
    for (const [navn, b] of navknapper) {
      const aktiv = navn === v;
      b.classList.toggle('aktiv', aktiv);
      b.setAttribute('aria-current', aktiv ? 'page' : 'false');
    }
  }

  function tegnVisning(): void {
    const v = visningAv(location.hash);
    // Vakta tilhørte skjermen vi forlater, og forsvinner med den.
    navvakt = null;
    merkAktiv(v);
    sett(hoved);
    switch (v) {
      case 'timer': visTimer(hoved, ctx); break;
      case 'kanban': visKanban(hoved, ctx); break;
      case 'sprinter': visSprinter(hoved, ctx); break;
      case 'sammendrag': visSammendrag(hoved, ctx); break;
      case 'inviter': visInviter(hoved, ctx); break;
      case 'meg': visMeg(hoved, ctx, {
        loggUt() {
          glemToken(lagre);
          tilInnlogging('Du er logget ut. Tokenet er slettet fra denne nettleseren.');
        },
      }); break;
    }
    kunngjører.si(`${VISNING_NAVN[v]} vises.`);
  }
}
